/**
 * How the floor plan is framed and paginated — no drawing, no fonts.
 *
 * Port of the planning half of `backend/app/services/pdf.py`. Kept apart from
 * the renderer so the export dialog can ask "how many pages would this room
 * take?" without loading a single font.
 *
 * A big room is paginated, not shrunk — and a floor plan is at most FOUR pages,
 * ever. One page while a first name clears 5pt; then two halves (where nearly
 * every big wedding stops); quarters only when half a page cannot print a 4pt
 * name at all. Every section prints at one shared scale, and cuts are placed in
 * the room's aisles by minimising the widest group.
 */

import { SEAT_PLATE_REACH, SEAT_RADIUS, tableBounds } from '../geometry'
import { type RenderPlan, type RenderTable, seatCountOf } from './model'

/** (x0, y0, x1, y1) */
export type Rect = readonly [number, number, number, number]

const CM = 72 / 2.54
export const PORTRAIT: readonly [number, number] = [21 * CM, 29.7 * CM]
export const LANDSCAPE: readonly [number, number] = [PORTRAIT[1], PORTRAIT[0]]
export const MARGIN = 34

/** Caps the upscale so a two-table plan doesn't balloon. */
export const MAX_UPSCALE = 1.8

/**
 * A guest's first name is set at 0.72 of the chair radius, so page scale
 * converts straight into type size — which is what decides whether a floor
 * plan is a seating chart or a diagram of one.
 */
export const NAME_PT_PER_SCALE = SEAT_RADIUS * 0.72
/** Below this a name is dropped rather than smudged. */
export const MIN_NAME_PT = 4
/** What the automatic page choice tries to buy by splitting. */
export const LEGIBLE_NAME_PT = 5
export const SPLIT_BELOW_SCALE = LEGIBLE_NAME_PT / NAME_PT_PER_SCALE

const GRIDS: Record<number, ReadonlyArray<readonly [cols: number, rows: number]>> = {
  2: [
    [1, 2],
    [2, 1],
  ],
  4: [
    [2, 2],
    [1, 4],
    [4, 1],
  ],
}
export const FLOOR_PAGE_CHOICES = [1, 2, 4] as const

/** Breathing room, in world px, drawn around the tables a section owns. */
export const SECTION_PADDING = 44

/** The band above the room reserved for the taken/free key. */
export const LEGEND_H = 13

export interface View {
  scale: number
  minX: number
  maxY: number
  offX: number
  offY: number
}

/** World y grows downward, PDF y grows upward. */
export function toPage(view: View, wx: number, wy: number): [number, number] {
  return [view.offX + (wx - view.minX) * view.scale, view.offY + (view.maxY - wy) * view.scale]
}

export interface FloorSection {
  number: number
  tableIds: string[]
  /** World rect of its tables, padded. */
  bounds: Rect
}

/**
 * World reach the chair *labels* need beyond the furniture. Only name plates
 * need any, and only when framing a page — `halfExtent` stays the furniture.
 */
export function labelPad(plan: RenderPlan): number {
  return plan.seatLabels === 'name' ? SEAT_PLATE_REACH : 0
}

export function tableBox(table: RenderTable, pad = 0): Rect {
  const b = tableBounds(table.shape, Math.max(seatCountOf(table), 1), table.x, table.y, table.rotation)
  return [b.minX - pad, b.minY - pad, b.maxX + pad, b.maxY + pad]
}

export function boundsOf(tables: RenderTable[], pad = 0): Rect {
  if (tables.length === 0) return [-400, -300, 400, 300]
  const boxes = tables.map((table) => tableBox(table, pad))
  return [
    Math.min(...boxes.map((b) => b[0])),
    Math.min(...boxes.map((b) => b[1])),
    Math.max(...boxes.map((b) => b[2])),
    Math.max(...boxes.map((b) => b[3])),
  ]
}

export const planBounds = (plan: RenderPlan): Rect => boundsOf(plan.tables, labelPad(plan))

/** Largest scale at which `world` still fits inside `area`. */
export function fitScale(world: Rect, area: Rect): number {
  return Math.min(
    (area[2] - area[0]) / Math.max(world[2] - world[0], 1),
    (area[3] - area[1]) / Math.max(world[3] - world[1], 1),
    MAX_UPSCALE,
  )
}

/** Centre `world` in `area`; a given `scale` keeps sibling pages comparable. */
export function fitView(world: Rect, area: Rect, scale?: number): View {
  const s = scale ?? fitScale(world, area)
  return {
    scale: s,
    minX: world[0],
    maxY: world[3],
    offX: area[0] + (area[2] - area[0] - (world[2] - world[0]) * s) / 2,
    offY: area[1] + (area[3] - area[1] - (world[3] - world[1]) * s) / 2,
  }
}

/** What the header consumes — known without drawing it. */
export const headerHeight = (plan: RenderPlan): number => (plan.eventDate || plan.venue ? 76 : 62)

/** Where the room goes on the whole-room page (overview or single page). */
export function floorArea(plan: RenderPlan): Rect {
  const [width, height] = LANDSCAPE
  return [MARGIN, MARGIN + 26, width - MARGIN, height - MARGIN - headerHeight(plan) - LEGEND_H]
}

/** Where the room goes on a section page — a compact header buys more room. */
export function sectionArea(): Rect {
  const [width, height] = LANDSCAPE
  return [MARGIN, MARGIN + 40, width - MARGIN, height - MARGIN - 30 - LEGEND_H]
}

/** A table with its world bounds, measured once. */
export type Placed = readonly [box: Rect, table: RenderTable]

export const place = (tables: RenderTable[], pad = 0): Placed[] =>
  tables.map((table) => [tableBox(table, pad), table] as const)

/**
 * Split into at most `count` groups along `axis` (0 = x, 1 = y), narrowest
 * widest group — so cuts land in the room's aisles instead of through a row of
 * tables. Binary search on the allowed extent: the greedy sweep uses the fewest
 * groups for a given extent.
 */
export function extentGroups(placed: readonly Placed[], count: number, axis: 0 | 1): Placed[][] {
  if (count <= 1 || placed.length <= 1) return [[...placed]]

  const spans = [...placed].sort(
    (a, b) => a[0][axis] - b[0][axis] || a[0][axis + 2] - b[0][axis + 2],
  )

  const sweep = (extent: number): Placed[][] => {
    const groups: Placed[][] = []
    let current: Placed[] = []
    let low = 0
    for (const item of spans) {
      if (current.length > 0 && item[0][axis + 2] - low > extent) {
        groups.push(current)
        current = []
      }
      if (current.length === 0) low = item[0][axis]
      current.push(item)
    }
    if (current.length > 0) groups.push(current)
    return groups
  }

  const widest =
    Math.max(...spans.map((item) => item[0][axis + 2])) - Math.min(...spans.map((item) => item[0][axis]))
  let low = 0
  let high = widest
  for (let i = 0; i < 24; i += 1) {
    const mid = (low + high) / 2
    if (sweep(mid).length <= count) high = mid
    else low = mid
  }
  return sweep(high)
}

/** Cut the room into bands of rows, then each band into columns. */
export function cutSections(placed: readonly Placed[], cols: number, rows: number): FloorSection[] {
  const sections: FloorSection[] = []
  for (const band of extentGroups(placed, rows, 1)) {
    for (const group of extentGroups(band, cols, 0)) {
      const members = group
        .map((item) => item[1])
        .map((table, order) => ({ table, order }))
        .sort((a, b) => a.table.y - b.table.y || a.table.x - b.table.x || a.order - b.order)
        .map(({ table }) => table)
      sections.push({
        number: sections.length + 1,
        tableIds: members.map((table) => table.id),
        bounds: [
          Math.min(...group.map((item) => item[0][0])) - SECTION_PADDING,
          Math.min(...group.map((item) => item[0][1])) - SECTION_PADDING,
          Math.max(...group.map((item) => item[0][2])) + SECTION_PADDING,
          Math.max(...group.map((item) => item[0][3])) + SECTION_PADDING,
        ],
      })
    }
  }
  return sections
}

/** The one scale every section page prints at — the tightest section decides. */
export function sectionScale(sections: readonly FloorSection[]): number {
  const area = sectionArea()
  return Math.min(...sections.map((section) => fitScale(section.bounds, area)))
}

/**
 * The sharpest way to break the room into (at most) `pages` sections, judged
 * by the scale the actual sections achieve. `null` when no grid produces more
 * than one section — a room with nowhere to cut.
 */
export function bestSplit(
  placed: readonly Placed[],
  pages: number,
): { scale: number; sections: FloorSection[] } | null {
  let best: { scale: number; sections: FloorSection[] } | null = null
  for (const [cols, rows] of GRIDS[pages] ?? []) {
    const sections = cutSections(placed, cols, rows)
    if (sections.length < 2) continue
    const scale = sectionScale(sections)
    if (
      best === null ||
      scale > best.scale ||
      (scale === best.scale && sections.length < best.sections.length)
    ) {
      best = { scale, sections }
    }
  }
  return best
}

/** 1, 2 or 4: the fewest floor-plan pages on which the room still reads. */
export function chooseFloorPages(plan: RenderPlan): number {
  if (plan.tables.length < 2) return 1
  if (fitScale(planBounds(plan), floorArea(plan)) >= SPLIT_BELOW_SCALE) return 1
  const placed = place(plan.tables, labelPad(plan))
  const halves = bestSplit(placed, 2)
  if (halves === null) return 1 // nowhere to cut: the single page is all there is
  if (halves.scale * NAME_PT_PER_SCALE >= MIN_NAME_PT) return 2
  return bestSplit(placed, 4) ? 4 : 2
}

/**
 * The room's section pages; `[]` means it belongs on one page. `floorPages`
 * forces a count (a budget, not a quota); omitted, the room decides.
 */
export function planSections(plan: RenderPlan, floorPages?: number | null): FloorSection[] {
  if (plan.tables.length < 2) return []
  const pages = floorPages || chooseFloorPages(plan)
  if (!GRIDS[pages]) return []
  return bestSplit(place(plan.tables, labelPad(plan)), pages)?.sections ?? []
}

export interface Split {
  sections: FloorSection[]
  scale: number
  world: Rect
  placed: Map<string, Placed>
}

/** `null` when the room belongs on one page, which is the common case. */
export function splitFor(plan: RenderPlan, floorPages?: number | null): Split | null {
  const sections = planSections(plan, floorPages)
  if (sections.length === 0) return null
  return {
    sections,
    scale: sectionScale(sections),
    world: planBounds(plan),
    placed: new Map(place(plan.tables, labelPad(plan)).map((item) => [item[1].id, item])),
  }
}

/** Does any part of this table land inside the drawn area? */
export function reachesPage(view: View, area: Rect, bounds: Rect): boolean {
  const [px0, py1] = toPage(view, bounds[0], bounds[1])
  const [px1, py0] = toPage(view, bounds[2], bounds[3])
  return px1 >= area[0] && px0 <= area[2] && py1 >= area[1] && py0 <= area[3]
}
